import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { isNativePlatform, registerForNativePush } from '../utils/nativePush';

// Web Push Certificate (VAPID) key from Firebase Console > Project Settings > Cloud Messaging
const VAPID_KEY = 'BLdEUWsM2_g4QgiQnNnquxwtea_ELWnzBm_p7FUndxNnfeR5fuI0FMi3quJzdlJ1VD_RmJq7hn_CXG2TfrjldBg';

// Firebase configuration - CheF Web App (from Firebase Console)
const firebaseConfig = {
  apiKey: "AIzaSyCxly5CbEUguNZ8OYf-_S0mvmSo7X0A2Xg",
  authDomain: "kfc-sk-6638f.firebaseapp.com",
  databaseURL: "https://kfc-sk-6638f-default-rtdb.firebaseio.com",
  projectId: "kfc-sk-6638f",
  storageBucket: "kfc-sk-6638f.firebasestorage.app",
  messagingSenderId: "153903088164",
  appId: "1:153903088164:web:9b89afbf90e622a95f4eb1",
  measurementId: "G-G00T1MGGVM"
};

// Initialize Firebase with error handling
let app = null;
let db = null;
let auth = null;
let functions = null;
let messaging = null;

try {
  app = initializeApp(firebaseConfig);
  // Safari blocks Firestore's fetch-streaming Listen channel with CORS
  // errors — force long polling there instead of letting auto-detect fail
  // noisily first. Android/Chrome WebViews keep the streaming channel.
  // persistentLocalCache is the web equivalent of the iOS CacheManager:
  // IndexedDB-backed document cache, offline reads, queued writes, and
  // cache-first snapshot delivery. Multi-tab manager keeps it safe when
  // the app is open in several tabs at once.
  const isSafari = /^((?!chrome|chromium|android).)*safari/i.test(navigator.userAgent);
  try {
    db = initializeFirestore(app, {
      ...(isSafari
        ? { experimentalForceLongPolling: true }
        : { experimentalAutoDetectLongPolling: true }),
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    });
  } catch {
    db = getFirestore(app); // already initialized by a portal module
  }
  auth = getAuth(app);
  functions = getFunctions(app);
  // Messaging is only available in supported browsers and secure contexts.
  if (typeof window !== 'undefined' && 'navigator' in window) {
    messaging = getMessaging(app);
  }
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Firebase initialization failed:', error);
}

const FirebaseContext = createContext();

export function FirebaseProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseError, setFirebaseError] = useState(null);

  useEffect(() => {
    if (!auth) {
      setFirebaseError('Firebase authentication not available');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground FCM message received:', payload);
      if (Notification.permission === 'granted') {
        const title = payload.notification?.title || payload.data?.title || 'CheF SKN';
        const options = {
          body: payload.notification?.body || payload.data?.body || '',
          icon: '/ChefLogo.jpg',
          badge: '/favicon.jpg',
          tag: payload.data?.tag || payload.messageId || 'chef-skn',
          data: payload.data || {}
        };
        new Notification(title, options);
      }
    });

    return () => unsubscribe();
  }, []);

  const requestNotificationPermissionAndSaveToken = async (user) => {
    if (isNativePlatform()) {
      return registerForNativePush(user, db);
    }
    if (!messaging || !user) {
      console.warn('Messaging not available or user not signed in');
      return;
    }

    // Safari only allows the permission prompt from a user gesture — skip
    // silently when none has happened; granted users still get their token.
    if (typeof Notification === 'undefined' || Notification.permission === 'denied') return;
    if (Notification.permission === 'default' &&
        navigator.userActivation && !navigator.userActivation.hasBeenActive) return;

    try {
      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied:', permission);
        return;
      }

      const token = await getToken(messaging, { vapidKey: VAPID_KEY });
      if (!token) {
        console.warn('No FCM token returned');
        return;
      }

      console.log('FCM web token:', token);

      const tokenDoc = doc(db, 'users', user.uid, 'fcmTokens', token);
      await setDoc(tokenDoc, {
        token,
        platform: 'web',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userAgent: navigator.userAgent
      }, { merge: true });

      console.log('FCM token saved to Firestore');
      return token;
    } catch (error) {
      console.error('Error requesting notification permission or saving token:', error);
    }
  };

  const value = {
    user,
    loading,
    firebaseError,
    db,
    auth,
    functions,
    messaging,
    requestNotificationPermissionAndSaveToken
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

export function useFirebase() {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
}
